import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal, ModalClose, Button, Icon, StatusPill, SearchInput, Pagination } from '../../campaign-os';
import { EmailPreview, EMAIL_CANVAS } from '../EmailPreview';
import { useWizard } from '../store/wizardStore';
import { useToast } from '../../../../components/ui/Toast';
import { useApiData } from '../data/useApiData';
import { PreSendCheck } from '../components/PreSendCheck';
import { vendorPill } from '../data/messageStatus';
import { normalizeAddress, toCcEntry, mergeCc } from '../data/ccList';
import { RecipientListEditor } from './RecipientListEditor';
import { templateService, campaignService, getErrorMessage, getErrorDetails } from '../../../../services/apiService';

/**
 * New Campaign — the REAL create-and-send flow.
 *   Details  → name the campaign
 *   Template → pick a saved EmailTemplate (GET /email-templates)
 *   Audience → filter the actual vendor table, SEE the matching vendors, and tick / untick
 *              them one by one. The list (GET /campaigns/audience), the id set
 *              (GET /campaigns/audience/ids) and the send all run the SAME query, so the
 *              number reviewed is the number emailed.
 *   Review   → the exact email, rendered by the send-time renderer, shown whole
 *   Launch   → POST /campaigns/broadcast, which queues a real send
 *
 * Send shape: "one email per vendor" gives every vendor their own personalised copy.
 * "one email, everyone in CC" sends a SINGLE message to one address and copies the whole
 * audience — one mail, one thread, one item in everyone's inbox. In that shape the
 * audience picked below becomes the CC list, which stays editable right up to launch.
 *
 * Selection model: every vendor the filters match is IN unless it was unticked. The
 * unticked ids travel with the request as `exclude_vendor_ids`, so a filter that matches
 * 3,000 vendors with two exceptions is still one small request.
 *
 * Nothing inside the dialog scrolls. The dialog grows to its content — the whole vendor
 * list, the whole email — and the page scrolls as one surface. The action bar is pinned
 * to the bottom of the viewport so Continue is always within reach.
 */

const STEPS = [
  { n: '01', key: 'details', label: 'Details' },
  { n: '02', key: 'template', label: 'Template' },
  { n: '03', key: 'audience', label: 'Audience' },
  { n: '04', key: 'review', label: 'Review' },
  { n: '05', key: 'launch', label: 'Launch' },
];

const LIST_PAGE = 50;
/**
 * The provider's ceiling on ONE mail, counting the recipient plus CC plus BCC together.
 *
 * Measured against Mailtrap rather than taken from its documentation, which claims 1000
 * per field: 1 recipient + 999 BCC is accepted, 1 + 1000 comes back 400 "a maximum of
 * 1000 recipients per request is allowed". A 400 is not retryable, so going over does not
 * degrade — it kills the campaign. Mirrors MAX_RECIPIENTS_PER_REQUEST on the API.
 */
const MAX_RECIPIENTS = 1000;
/** What is left for the copy lists once the single recipient has taken its place. */
const MAX_CC = MAX_RECIPIENTS - 1;
/** CC rows shown before the "show all" toggle — the dialog itself never scrolls. */
const CC_PREVIEW_ROWS = 12;
const SEARCH_DEBOUNCE_MS = 300;
const COUNT_DEBOUNCE_MS = 350;

const FIELD =
  'w-full rounded-xl border border-cos-line bg-white/70 px-3 py-2.5 text-sm text-cos-ink placeholder:text-cos-ink-secondary/60 focus:border-cos-teal/50 focus:outline-none focus:ring-2 focus:ring-cos-teal/15 transition';
const LABEL = 'mb-1.5 block text-[13px] font-semibold text-cos-ink';
const RAIL_LABEL = 'mb-1 block text-[11px] font-semibold uppercase tracking-[0.08em] text-cos-ink-muted';
const RAIL_FIELD =
  'w-full rounded-lg border border-cos-line bg-white/80 px-2.5 py-2 text-[13px] text-cos-ink focus:border-cos-teal/50 focus:outline-none focus:ring-2 focus:ring-cos-teal/15 transition';
const CHECKBOX = 'h-4 w-4 cursor-pointer rounded border-cos-line accent-[#0A9188]';

/** "All" means "do not constrain on this column". */
const ALL = 'All';
const asFilterValue = (v) => (v && v !== ALL ? v : undefined);
const num = (n) => Number(n || 0).toLocaleString('en-IN');
const MSME_LABEL = { MSME: 'MSME', NON_MSME: 'Non-MSME', PENDING: 'Pending' };

/** Flatten the API's field-level validation details into one readable line. */
function describeError(err, fallback) {
  const details = getErrorDetails(err);
  if (details && typeof details === 'object') {
    const parts = Object.entries(details).map(
      ([field, msgs]) => `${field.replace(/^(body|query|params)\./, '')}: ${[].concat(msgs).join(', ')}`
    );
    if (parts.length) return parts.join(' · ');
  }
  return getErrorMessage(err) || fallback;
}

function RailSelect({ label, value, onChange, options, humanise }) {
  return (
    <div>
      <label className={RAIL_LABEL}>{label}</label>
      <select className={RAIL_FIELD} value={value} onChange={(e) => onChange(e.target.value)}>
        <option value={ALL}>All</option>
        {options.map((o) => {
          const v = typeof o === 'string' ? o : o.value;
          const count = typeof o === 'string' ? null : o.count;
          return (
            <option key={v} value={v}>
              {humanise ? humanise(v) : v}
              {count != null ? ` (${count})` : ''}
            </option>
          );
        })}
      </select>
    </div>
  );
}

/** Tri-state header checkbox (all / some / none of the rows on this page are ticked). */
function PageCheckbox({ all, some, onChange, disabled }) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = !all && some;
  }, [all, some]);
  return (
    <input
      ref={ref}
      type="checkbox"
      className={CHECKBOX}
      checked={all}
      disabled={disabled}
      onChange={onChange}
      aria-label={all ? 'Untick every vendor on this page' : 'Tick every vendor on this page'}
    />
  );
}

export function NewCampaignWizard() {
  const { isOpen, close } = useWizard();
  const toast = useToast();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);

  const [name, setName] = useState('');
  const [templateId, setTemplateId] = useState(null);

  const [status, setStatus] = useState('ACTIVE');
  const [country, setCountry] = useState(ALL);
  const [category, setCategory] = useState(ALL);
  const [type, setType] = useState(ALL);
  const [msme, setMsme] = useState(ALL);
  const [region, setRegion] = useState(ALL);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Vendors the operator unticked. Kept across filter changes: an exclusion is a decision
  // about a vendor, not about a filter. Ids that no longer match are simply ignored.
  const [excluded, setExcluded] = useState(() => new Set());
  const [listPage, setListPage] = useState(1);

  // How the audience is chosen: the live filters, or vendor codes imported from a
  // spreadsheet ("the lot"). Two separate untick-sets, because an exclusion is a
  // decision about a vendor within a mode, not a global one.
  const [audienceMode, setAudienceMode] = useState('filters');
  const [importData, setImportData] = useState(null);
  const [importFileName, setImportFileName] = useState('');
  const [importing, setImporting] = useState(false);
  const [importExcluded, setImportExcluded] = useState(() => new Set());
  const [importPage, setImportPage] = useState(1);
  const importFileRef = useRef(null);

  // How the mail is addressed. 'per_vendor' is the default and what Regularization does.
  // 'single_cc' sends one message to `toEmail` and copies the whole audience.
  const [sendMode, setSendMode] = useState('per_vendor');
  // An address several vendor records share (a group mailbox, an agent, one company's
  // plants) gets one mail greeting every vendor it covers, not one mail per vendor.
  const [onePerAddress, setOnePerAddress] = useState(true);
  const [toEmail, setToEmail] = useState('');
  const [toName, setToName] = useState('');
  // How the mail opens. 'vendor' and 'vendor_code' are merge tags filled in per recipient;
  // 'fixed' is the operator's own words, the same for everyone. A shared send has one body
  // for the whole list, so it can only ever be 'fixed'.
  const [greetingMode, setGreetingMode] = useState('vendor');
  const [greetingText, setGreetingText] = useState('Dear Partners,');

  // The CC list. In per_vendor mode it is the handful of operators who want a copy of
  // every email; in single_cc mode it IS the audience. Same editor either way.
  const [ccEnabled, setCcEnabled] = useState(false);
  const [ccEntries, setCcEntries] = useState([]);
  const [bccEntries, setBccEntries] = useState([]);
  const [ccLoading, setCcLoading] = useState(false);
  const [bccLoading, setBccLoading] = useState(false);
  const [ccCapped, setCcCapped] = useState(false);
  const [bccCapped, setBccCapped] = useState(false);

  const [preview, setPreview] = useState(null);
  // What a launch would walk into. Advisory: it never blocks the send.
  const [precheck, setPrecheck] = useState(null);
  const [prechecking, setPrechecking] = useState(false);
  // A shared send whose one request is larger than the account's hourly cap can never be
  // paced — the provider would block the rest and lose it — so the button waits for a split.
  const hourlyBlocked = Boolean(precheck?.hourly && precheck.hourly.fits_in_one_request === false);
  const [counting, setCounting] = useState(false);
  const [sending, setSending] = useState(false);

  const templates = useApiData(() => templateService.list({}), [], { enabled: isOpen });
  const templateRows = templates.data?.data ?? [];
  const chosen = templateRows.find((t) => t.id === templateId) ?? null;

  // Real option lists straight off the vendor table.
  const audienceOptions = useApiData(() => campaignService.audience({ limit: 1 }), [], { enabled: isOpen });
  const opts = audienceOptions.data?.options ?? {};
  const totalVendors = audienceOptions.data?.total ?? 0;

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [search]);

  const filter = useMemo(
    () => ({
      status: asFilterValue(status),
      country: asFilterValue(country),
      category: asFilterValue(category),
      type: asFilterValue(type),
      msme: asFilterValue(msme),
      region: asFilterValue(region),
      search: debouncedSearch || undefined,
    }),
    [status, country, category, type, msme, region, debouncedSearch]
  );
  const filterKey = JSON.stringify(filter);

  // Any filter change starts the list from page 1.
  useEffect(() => {
    setListPage(1);
  }, [filterKey]);

  // The matching id set — what "select all", "clear" and the live count are computed from.
  const ids = useApiData(() => campaignService.audienceIds(filter), [filterKey], { enabled: isOpen && step >= 2 });
  const matchingIds = useMemo(() => ids.data?.data?.ids ?? [], [ids.data]);
  const excludedMatching = useMemo(() => matchingIds.filter((id) => excluded.has(id)), [matchingIds, excluded]);
  const filterSelectedCount = matchingIds.length - excludedMatching.length;

  const importRows = useMemo(() => importData?.matched ?? [], [importData]);
  const importSelectedIds = useMemo(
    () => importRows.filter((v) => !importExcluded.has(v.id)).map((v) => v.id),
    [importRows, importExcluded]
  );
  const selectedCount = audienceMode === 'import' ? importSelectedIds.length : filterSelectedCount;

  // In single_cc mode the CC list is the whole point, so it is never gated on the
  // opt-in checkbox that per_vendor mode uses.
  const ccActive = sendMode === 'single_cc' || ccEnabled;
  const ccList = useMemo(
    () => (ccActive ? ccEntries.map((e) => e.email).slice(0, MAX_CC) : []),
    [ccActive, ccEntries]
  );
  const bccList = useMemo(
    () => (ccActive ? bccEntries.map((e) => e.email).slice(0, MAX_CC) : []),
    [ccActive, bccEntries]
  );
  /** Everyone the send reaches beyond the TO, however they are copied. */
  const copiedCount = ccList.length + bccList.length;
  /** Both copy lists as one, for the "promote someone to TO" picker. */
  const copyList = useMemo(() => mergeCc(ccEntries, bccEntries), [ccEntries, bccEntries]);

  // The visible page of matching vendors.
  const list = useApiData(
    () => campaignService.audience({ ...filter, page: listPage, limit: LIST_PAGE }),
    [filterKey, listPage],
    { enabled: isOpen && step === 2 }
  );
  const rows = list.data?.data ?? [];
  const listMeta = list.data?.meta ?? { page: listPage, limit: LIST_PAGE, total: rows.length };

  const pageAllTicked = rows.length > 0 && rows.every((v) => !excluded.has(v.id));
  const pageSomeTicked = rows.some((v) => !excluded.has(v.id));

  const toggleVendor = (id) =>
    setExcluded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const togglePage = () =>
    setExcluded((prev) => {
      const next = new Set(prev);
      if (pageAllTicked) rows.forEach((v) => next.add(v.id));
      else rows.forEach((v) => next.delete(v.id));
      return next;
    });
  const selectAllMatching = () => setExcluded(new Set());
  const clearSelection = () => setExcluded(new Set(matchingIds));

  const toggleImportVendor = (id) =>
    setImportExcluded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const handleImportFile = async (file) => {
    if (!file) return;
    setImporting(true);
    try {
      const res = await campaignService.importAudience(file);
      if (res.success) {
        setImportData(res.data);
        setImportFileName(file.name);
        setImportExcluded(new Set());
        setImportPage(1);
        const d = res.data;
        toast.success(
          `${num(d.matched.length)} of ${num(d.total_codes)} codes matched vendors` +
            (d.missing.length ? ` — ${num(d.missing.length)} not in the system` : '')
        );
      }
    } catch (err) {
      toast.error(describeError(err, 'Could not read that spreadsheet'));
    } finally {
      setImporting(false);
      if (importFileRef.current) importFileRef.current.value = '';
    }
  };

  // ---- the CC list ---------------------------------------------------------------

  /** The selected vendors, with addresses — the API only for the filter mode. */
  const selectedRecipientRows = async () => {
    if (audienceMode === 'import') return importRows.filter((v) => !importExcluded.has(v.id));
    const res = await campaignService.audienceRecipients(filter);
    setCcCapped(Boolean(res.data?.capped));
    return (res.data?.recipients ?? []).filter((v) => !excluded.has(v.id));
  };

  /**
   * Pull the selected audience into a copy list, keeping anything already there.
   * CC and BCC share this: which list it lands in is the only difference between them.
   */
  const fillFromAudience = (setEntries, setLoading) => async () => {
    setLoading(true);
    try {
      const vendors = await selectedRecipientRows();
      setEntries((prev) => mergeCc(prev, vendors.map(toCcEntry)));
    } catch (err) {
      toast.error(describeError(err, 'Could not read the selected vendors'));
    } finally {
      setLoading(false);
    }
  };

  /**
   * Upload an edited copy-list sheet. This reuses the audience importer: coded rows
   * resolve against the live vendor table (the database stays the master for a vendor's
   * address) and rows with only an address come back as extra_emails, which is what lets
   * an address typed into the sheet by hand survive the round trip.
   */
  const uploadSheet = (setEntries, setLoading) => async (file) => {
    if (!file) return;
    setLoading(true);
    try {
      const res = await campaignService.importAudience(file);
      const d = res.data ?? {};
      const fromSheet = [
        ...(d.matched ?? []).map(toCcEntry),
        ...(d.extra_emails ?? []).map((email) => ({ email, label: '', vendor_code: null })),
      ];
      setEntries((prev) => mergeCc(prev, fromSheet));
      toast.success(
        `${num(fromSheet.length)} address${fromSheet.length === 1 ? '' : 'es'} read` +
          (d.missing?.length ? ` — ${num(d.missing.length)} code(s) not in the system` : '')
      );
    } catch (err) {
      toast.error(describeError(err, 'Could not read that spreadsheet'));
    } finally {
      setLoading(false);
    }
  };

  const resetFilters = () => {
    setStatus('ACTIVE');
    setCountry(ALL);
    setCategory(ALL);
    setType(ALL);
    setMsme(ALL);
    setRegion(ALL);
    setSearch('');
  };

  // Reset everything whenever the wizard is reopened.
  useEffect(() => {
    if (!isOpen) return;
    setStep(0);
    setName('');
    setTemplateId(null);
    resetFilters();
    setExcluded(new Set());
    setListPage(1);
    setPreview(null);
    setPrecheck(null);
    setAudienceMode('filters');
    setImportData(null);
    setImportFileName('');
    setImportExcluded(new Set());
    setImportPage(1);
    setCcEnabled(false);
    setSendMode('per_vendor');
    setOnePerAddress(true);
    setToEmail('');
    setToName('');
    setGreetingMode('vendor');
    setGreetingText('Dear Partners,');
    setCcEntries([]);
    setBccEntries([]);
    setCcCapped(false);
    setBccCapped(false);
  }, [isOpen]);

  const GREETING_BY_MODE = {
    vendor: 'Dear {{params.company_name}},',
    vendor_code: 'Dear {{params.company_name}} ({{params.vendor_code}}),',
  };
  const effectiveGreetingMode = sendMode === 'single_cc' ? 'fixed' : greetingMode;
  /**
   * The greeting this send will open with, or nothing when it is the default for its
   * shape — so a template that carries its own greeting still gets to use it, and the
   * server's long-standing defaults apply otherwise. Only an operator's choice travels.
   */
  const greetingOverride = useMemo(() => {
    const text = effectiveGreetingMode === 'fixed' ? greetingText.trim() : GREETING_BY_MODE[effectiveGreetingMode];
    const shapeDefault = sendMode === 'single_cc' ? 'Dear Partners,' : GREETING_BY_MODE.vendor;
    return text && text !== shapeDefault ? text : null;
  }, [effectiveGreetingMode, greetingText, sendMode]);

  /** Everything the server needs to resolve the audience — identical for preview and launch. */
  const audienceBody = useMemo(
    () =>
      audienceMode === 'import'
        ? { vendor_ids: importSelectedIds }
        : { filter, ...(excludedMatching.length ? { exclude_vendor_ids: excludedMatching } : {}) },
    [audienceMode, importSelectedIds, filter, excludedMatching]
  );

  /** Count + render together: one call, and it writes nothing. */
  /**
   * Ask the server what this send would walk into: addresses the provider will refuse,
   * and addresses that have already bounced. Never blocks the launch — the operator may
   * have a reason — and never fails it either, so a precheck outage cannot stop a send.
   */
  const runPrecheck = async () => {
    setPrechecking(true);
    try {
      const res = await campaignService.precheckBroadcast({
        ...sendBody,
        template_id: templateId,
        ...(greetingOverride ? { greeting: greetingOverride } : {}),
        name: name.trim() || 'precheck',
        ...(ccList.length ? { cc: ccList } : {}),
        ...(bccList.length ? { bcc: bccList } : {}),
      });
      if (res.success) setPrecheck(res.data);
    } catch {
      setPrecheck(null);
    } finally {
      setPrechecking(false);
    }
  };

  const runPreview = async () => {
    setCounting(true);
    try {
      const res = await campaignService.previewBroadcast({
        ...audienceBody,
        template_id: templateId,
        ...(greetingOverride ? { greeting: greetingOverride } : {}),
        name: name.trim() || undefined,
        // Preview the SHAPE that will be sent while still counting the vendor audience:
        // a shared copy greets everyone at once and carries no per-vendor merge tags.
        ...(sendMode === 'single_cc' ? { shared_audience: true } : {}),
      });
      if (res.success) {
        setPreview(res.data);
        return res.data;
      }
    } catch (err) {
      toast.error(describeError(err, 'Could not build the campaign preview'));
    } finally {
      setCounting(false);
    }
    return null;
  };

  // The greeting is chosen on the Review step, after the preview was rendered on the way in.
  // Re-render when it changes, so the mail on screen is the mail that will go — a preview
  // that ignores the choice just made beside it is worse than no preview.
  const previewedGreeting = useRef(undefined);
  useEffect(() => {
    if (!isOpen || step !== 3) return undefined;
    if (previewedGreeting.current === greetingOverride) return undefined;
    const t = setTimeout(() => {
      previewedGreeting.current = greetingOverride;
      void runPreview();
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, step, greetingOverride]);

  // Keep the server-side count in step with the filters and ticks while on the Audience step.
  useEffect(() => {
    if (!isOpen || step !== 2) return undefined;
    if (audienceMode === 'import' && importSelectedIds.length === 0) return undefined;
    let cancelled = false;
    const t = setTimeout(async () => {
      setCounting(true);
      try {
        const res = await campaignService.previewBroadcast({
          ...audienceBody,
          template_id: templateId,
        ...(greetingOverride ? { greeting: greetingOverride } : {}),
          ...(sendMode === 'single_cc' ? { shared_audience: true } : {}),
        });
        if (!cancelled && res.success) setPreview(res.data);
      } catch {
        /* the Continue button re-runs this and surfaces any real error */
      } finally {
        if (!cancelled) setCounting(false);
      }
    }, COUNT_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [isOpen, step, audienceBody, templateId, audienceMode, importSelectedIds.length, sendMode]);

  // The copy lists are final once the Review step is behind us, so the check runs on the
  // way into Launch — the last screen before the send, and the only one where knowing
  // about a dead mailbox can still change what the operator does.
  useEffect(() => {
    if (!isOpen || step !== 4) return;
    runPrecheck();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, step]);

  /**
   * What LAUNCH sends. In single_cc mode the vendors are not the recipients — they are
   * the CC list — so the body names one literal address instead of a vendor audience.
   * Preview keeps using the vendor audience: it writes nothing, and the rendered mail is
   * the same either way.
   */
  const sendBody = useMemo(
    () =>
      sendMode === 'single_cc'
        ? { to_email: toEmail.trim(), ...(toName.trim() ? { to_name: toName.trim() } : {}) }
        : { ...audienceBody, one_copy_per_address: onePerAddress },
    [sendMode, toEmail, toName, audienceBody, onePerAddress]
  );

  // Every campaign mail carries {{params.…}} tags whether or not the chosen template has
  // any: the shared broadcast layout hardcodes "Dear {{params.company_name}}," into all
  // of them. So their presence says nothing about the operator's choice and must NOT
  // block the send — it only means the greeting is generic, which is stated below.
  /** Why launch is blocked in single_cc mode, or null when it is ready. */
  const ccBlocker =
    sendMode !== 'single_cc'
      ? null
      : !normalizeAddress(toEmail)
        ? 'Enter the address this one email goes to.'
        : copiedCount === 0
          ? 'Nobody is copied — fill CC or BCC from the audience, or add addresses.'
          : 1 + ccList.length + bccList.length > MAX_RECIPIENTS
            ? `One mail carries at most ${num(MAX_RECIPIENTS)} addresses in total — the recipient plus ` +
              `CC plus BCC. This one has ${num(1 + ccList.length + bccList.length)}. ` +
              'Split the list across more than one campaign.'
            : null;

  const audienceReady =
    audienceMode === 'import'
      ? importSelectedIds.length > 0
      : !ids.loading && matchingIds.length > 0 && selectedCount > 0;
  const canContinue =
    (step === 0 && name.trim().length >= 2) ||
    (step === 1 && templateId != null) ||
    (step === 2 && audienceReady) ||
    (step === 3 && !ccBlocker);

  const next = async () => {
    if (step === 2) {
      if (selectedCount === 0) {
        toast.warning('No vendors selected — widen the filters or tick some vendors');
        return;
      }
      const p = await runPreview();
      if (!p || p.recipient_count === 0) {
        toast.warning('No vendors match this audience');
        return;
      }
      setStep(3);
      return;
    }
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };

  /**
   * Take a set of addresses off both copy lists. Offered only for the ones a re-send
   * cannot help — a refused address and a dead mailbox stay dead — so the operator can
   * clean the list here instead of launching at it and collecting the bounce.
   *
   * Vendors in the audience are deliberately NOT removed: dropping somebody from a send
   * is the operator's decision to make on the Audience step, not a side effect of a
   * warning panel.
   */
  const dropAddressesFromCopyLists = (addresses) => {
    const drop = new Set(addresses.map((a) => String(a).trim().toLowerCase()));
    const keep = (list) => list.filter((e) => !drop.has(String(e.email).trim().toLowerCase()));
    setCcEntries(keep);
    setBccEntries(keep);
    setPrecheck((p) => (p ? { ...p, problems: p.problems.filter((x) => !drop.has(x.email.toLowerCase())) } : p));
    toast.success(`${num(drop.size)} address${drop.size === 1 ? '' : 'es'} removed from the copy lists`);
  };

  const launch = async () => {
    setSending(true);
    try {
      const res = await campaignService.sendBroadcast({
        ...sendBody,
        template_id: templateId,
        ...(greetingOverride ? { greeting: greetingOverride } : {}),
        name: name.trim(),
        ...(ccList.length ? { cc: ccList } : {}),
        ...(bccList.length ? { bcc: bccList } : {}),
      });
      if (res.success) {
        // The server drops copy addresses no provider could deliver to rather than
        // refusing the whole send, so report what it actually took, not what we offered.
        const leftOut = res.data.skipped_addresses ?? [];
        const copied = res.data.copied ?? copiedCount;
        toast.success(
          sendMode === 'single_cc'
            ? `“${name.trim()}” sent as one email to ${toEmail.trim()}, copying ${num(copied)} — tracking in Campaigns`
            : `“${name.trim()}” queued for ${num(res.data.queued)} vendor(s) — tracking in Campaigns`
        );
        if (leftOut.length > 0) {
          // Named, not counted: the operator has to be able to go and fix the records.
          const shown = leftOut.slice(0, 3).join(', ');
          const rest = leftOut.length - 3;
          toast.warning(
            `${num(leftOut.length)} address${leftOut.length === 1 ? '' : 'es'} could not be sent to — ` +
              `${shown}${rest > 0 ? ` and ${num(rest)} more` : ''}. ` +
              'They are on the campaign as failed, so you can fix the vendor record and send again.'
          );
        }
        close();
        navigate('/campaigning-tool/campaigns', { state: { refreshAt: Date.now() } });
      }
    } catch (err) {
      toast.error(describeError(err, 'Could not launch the campaign'));
    } finally {
      setSending(false);
    }
  };

  if (!isOpen) return null;

  const activeFilters = Object.values(filter).filter(Boolean).length;

  // One table serves both audience modes; only its row source differs.
  const importPageRows = importRows.slice((importPage - 1) * LIST_PAGE, importPage * LIST_PAGE);
  const tableRows = audienceMode === 'import' ? importPageRows : rows;
  const tableMeta =
    audienceMode === 'import' ? { page: importPage, limit: LIST_PAGE, total: importRows.length } : listMeta;
  const isTicked = (id) => (audienceMode === 'import' ? !importExcluded.has(id) : !excluded.has(id));
  const toggleAny = (id) => (audienceMode === 'import' ? toggleImportVendor(id) : toggleVendor(id));
  const tablePageAll = tableRows.length > 0 && tableRows.every((v) => isTicked(v.id));
  const tablePageSome = tableRows.some((v) => isTicked(v.id));
  const toggleTablePage = () => {
    if (audienceMode !== 'import') return togglePage();
    setImportExcluded((prev) => {
      const next = new Set(prev);
      if (tablePageAll) tableRows.forEach((v) => next.add(v.id));
      else tableRows.forEach((v) => next.delete(v.id));
      return next;
    });
  };
  const setTablePage = audienceMode === 'import' ? setImportPage : setListPage;
  const tableLoading = audienceMode === 'filters' && list.loading && !list.data;
  const recipientCount = preview?.recipient_count ?? selectedCount;
  const audienceSummary =
    audienceMode === 'import'
      ? `${num(recipientCount)} vendor${recipientCount === 1 ? '' : 's'} from ${importFileName || 'the imported sheet'}` +
        (importExcluded.size ? ` (${num(importExcluded.size)} unticked)` : '')
      : `${num(recipientCount)} vendor${recipientCount === 1 ? '' : 's'}${
          excludedMatching.length ? ` (${num(matchingIds.length)} matched, ${num(excludedMatching.length)} unticked)` : ''
        }`;
  return (
    <Modal open onClose={() => !sending && close()} size="full" labelledBy="wiz-title">
      <div className="flex flex-col">
        <div className="flex items-start justify-between gap-3 px-6 pt-6">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[1.4px] text-cos-ink-secondary">New campaign</p>
            <h2 id="wiz-title" className="text-xl font-bold text-cos-ink">Create vendor outreach</h2>
          </div>
          <ModalClose onClose={() => !sending && close()} />
        </div>

        {/* stepper */}
        <div className="flex flex-wrap items-center gap-2 px-6 py-4">
          {STEPS.map((s, i) => (
            <React.Fragment key={s.key}>
              <div
                className={[
                  'flex items-center gap-2 rounded-xl border px-3 py-2 text-sm font-semibold',
                  i < step
                    ? 'border-cos-teal/30 bg-cos-teal-faint/50 text-cos-teal-deep'
                    : i === step
                      ? 'border-cos-teal bg-white text-cos-ink shadow-cos-teal-glow'
                      : 'border-cos-line bg-white/40 text-cos-ink-secondary',
                ].join(' ')}
              >
                {i < step ? <Icon name="check" className="text-[17px]" /> : <span className="mono text-[12px]">{s.n}</span>}
                <span>{s.label}</span>
              </div>
              {i < STEPS.length - 1 ? <Icon name="chevron_right" className="text-[18px] text-cos-ink-muted" /> : null}
            </React.Fragment>
          ))}
        </div>

        <div className="px-6 pb-3">
          {/* ---------------------------------------------------------- 1 details */}
          {step === 0 ? (
            <div className="flex flex-col gap-4 py-2">
              <div>
                <label className={LABEL}>Campaign name</label>
                <input
                  className={FIELD}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Q3 bank detail re-verification"
                  autoFocus
                />
                <p className="mt-1 text-[12px] text-cos-ink-secondary">
                  Internal label shown in the Campaigns list. Vendors never see it — they see the template’s subject line.
                </p>
              </div>
            </div>
          ) : null}

          {/* ---------------------------------------------------------- 2 template */}
          {step === 1 ? (
            <div className="py-2">
              {templates.loading ? <p className="text-sm text-cos-ink-secondary">Loading templates…</p> : null}
              {!templates.loading && templateRows.length === 0 ? (
                <div className="flex flex-col items-center gap-3 py-10 text-center">
                  <Icon name="dashboard_customize" className="text-[34px] text-cos-ink-secondary/50" />
                  <p className="max-w-[320px] text-sm text-cos-ink-secondary">
                    No templates yet. Create one on the Templates screen first — a campaign always sends a template.
                  </p>
                </div>
              ) : null}
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
                {templateRows.map((t) => {
                  const active = t.id === templateId;
                  return (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => setTemplateId(t.id)}
                      className={[
                        'rounded-xl border p-4 text-left transition',
                        active
                          ? 'border-cos-teal bg-cos-teal-faint/40 shadow-cos-teal-glow'
                          : 'border-cos-line bg-white/60 hover:border-cos-teal/40',
                      ].join(' ')}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-cos-status-teal-bg">
                          <Icon name="mail" className="text-[17px] text-cos-teal-deep" />
                        </span>
                        {active ? <Icon name="check_circle" filled className="text-[19px] text-cos-teal-deep" /> : null}
                      </div>
                      <p className="mt-2 text-[14px] font-bold text-cos-ink">{t.name}</p>
                      <p className="mt-0.5 line-clamp-2 text-[12px] leading-[18px] text-cos-ink-secondary">
                        {t.description || t.subject}
                      </p>
                      <p className="mono mt-2 text-[11px] text-cos-ink-secondary">
                        {t.category}
                        {t.mode === 'scratch' ? ' · Scratch pad' : ''}
                      </p>
                    </button>
                  );
                })}
              </div>
            </div>
          ) : null}

          {/* ---------------------------------------------------------- 3 audience */}
          {step === 2 ? (
            <div className="flex flex-col gap-3 py-1">
              {/* How the mail is addressed. This changes what the selection below MEANS:
                  recipients in one shape, the CC list in the other. */}
              <div className="flex flex-wrap items-center gap-2 rounded-xl border border-cos-line bg-white/60 px-4 py-3">
                <span className="text-[11px] font-semibold uppercase tracking-[1.2px] text-cos-ink-secondary">
                  Send as
                </span>
                {[
                  ['per_vendor', 'forward_to_inbox', 'One email per vendor'],
                  ['single_cc', 'group', 'One email, everyone in CC'],
                ].map(([mode, icon, label]) => (
                  <button
                    key={mode}
                    type="button"
                    onClick={() => setSendMode(mode)}
                    className={[
                      'flex items-center gap-1.5 rounded-full border px-4 py-1.5 text-[13px] font-semibold transition-colors',
                      sendMode === mode
                        ? 'border-cos-teal-deep bg-cos-teal-faint/60 text-cos-teal-deep'
                        : 'border-cos-line text-cos-ink-secondary hover:text-cos-ink',
                    ].join(' ')}
                  >
                    <Icon name={icon} className="text-[16px]" />
                    {label}
                  </button>
                ))}
                {sendMode === 'single_cc' ? (
                  <span className="text-[12px] leading-[17px] text-cos-ink-secondary">
                    One message to one address, copying everyone selected below. Address the TO and edit the
                    CC list on the Review step.
                  </span>
                ) : (
                  <label className="flex w-full cursor-pointer items-start gap-2 pt-1 text-[12px] leading-[17px] text-cos-ink-secondary">
                    <input
                      type="checkbox"
                      checked={onePerAddress}
                      onChange={(e) => setOnePerAddress(e.target.checked)}
                      className="mt-0.5 accent-cos-teal-deep"
                    />
                    <span>
                      <span className="font-semibold text-cos-ink">One copy per address.</span> When several vendor records share
                      an address (a group mailbox, an agent, one company&apos;s plants), it gets one mail that greets every vendor it
                      covers, instead of the same mail once per vendor. Untick it when each vendor must get its own copy.
                    </span>
                  </label>
                )}
              </div>

              {/* How the audience is chosen. */}
              <div className="flex flex-wrap items-center gap-2">
                {[
                  ['filters', 'filter_alt', 'Filter the vendor base'],
                  ['import', 'upload_file', 'Import from Excel'],
                ].map(([mode, icon, label]) => (
                  <button
                    key={mode}
                    type="button"
                    onClick={() => setAudienceMode(mode)}
                    className={[
                      'flex items-center gap-1.5 rounded-full border px-4 py-1.5 text-[13px] font-semibold transition-colors',
                      audienceMode === mode
                        ? 'border-cos-teal-deep bg-cos-teal-faint/60 text-cos-teal-deep'
                        : 'border-cos-line text-cos-ink-secondary hover:text-cos-ink',
                    ].join(' ')}
                  >
                    <Icon name={icon} className="text-[16px]" />
                    {label}
                  </button>
                ))}
                {audienceMode === 'import' && (
                  <span className="text-[12px] text-cos-ink-secondary">
                    Upload the lot sheet — the vendor-code column is found automatically and every code is matched
                    against the live vendor database.
                  </span>
                )}
              </div>

            <div className="flex items-start gap-4">
              {/* Left rail: what is selected, and the filters that shape it. Sticks while the
                  list scrolls past, so the filters are always at hand. */}
              <aside className="sticky top-4 flex w-[296px] shrink-0 flex-col gap-3">
                <div className="rounded-xl border border-cos-teal/25 bg-cos-teal-faint/40 p-4">
                  <p className="text-[11px] font-semibold uppercase tracking-[1.4px] text-cos-ink-secondary">Selected audience</p>
                  <p className="mono mt-1 text-[38px] font-bold leading-none text-cos-teal-deep">
                    {audienceMode === 'filters' && ids.loading && !ids.data ? '…' : num(selectedCount)}
                  </p>
                  <p className="mt-1.5 text-[12px] leading-[17px] text-cos-ink-secondary">
                    {audienceMode === 'import'
                      ? importData
                        ? `${num(importRows.length)} of ${num(importData.total_codes)} codes matched` +
                          (importData.missing.length ? ` · ${num(importData.missing.length)} not in the system` : '') +
                          (importExcluded.size ? ` · ${num(importExcluded.size)} unticked` : '')
                        : 'Upload a sheet to build the audience'
                      : ids.loading && !ids.data
                        ? 'Counting…'
                        : `${num(matchingIds.length)} match the filters` +
                          (excludedMatching.length ? ` · ${num(excludedMatching.length)} unticked` : '') +
                          ` · ${num(totalVendors)} in the database`}
                  </p>
                  <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-white/70">
                    <div
                      className="h-full rounded-full bg-gradient-to-r from-cos-teal-bright to-cos-teal-deep transition-[width] duration-300"
                      style={{
                        width: `${(() => {
                          const denom = audienceMode === 'import' ? importRows.length : matchingIds.length;
                          return denom ? Math.max(selectedCount > 0 ? 2 : 0, (selectedCount / denom) * 100) : 0;
                        })()}%`,
                      }}
                    />
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {audienceMode === 'import' ? (
                      <>
                        <Button
                          variant="secondary"
                          size="sm"
                          icon="select_all"
                          onClick={() => setImportExcluded(new Set())}
                          disabled={importExcluded.size === 0}
                        >
                          Select all {num(importRows.length)}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          icon="deselect"
                          onClick={() => setImportExcluded(new Set(importRows.map((v) => v.id)))}
                          disabled={selectedCount === 0}
                        >
                          Clear
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button variant="secondary" size="sm" icon="select_all" onClick={selectAllMatching} disabled={excludedMatching.length === 0}>
                          Select all {num(matchingIds.length)}
                        </Button>
                        <Button variant="ghost" size="sm" icon="deselect" onClick={clearSelection} disabled={selectedCount === 0}>
                          Clear
                        </Button>
                      </>
                    )}
                  </div>
                </div>

                {audienceMode === 'import' ? (
                  <div className="rounded-xl border border-cos-line bg-white/50 p-4">
                    <div className="mb-3 flex items-center gap-1.5 text-[13px] font-bold text-cos-ink">
                      <Icon name="upload_file" className="text-[18px] text-cos-teal-deep" />
                      Excel import
                    </div>
                    <input
                      ref={importFileRef}
                      type="file"
                      accept=".xlsx,.xls,.csv"
                      className="hidden"
                      onChange={(e) => handleImportFile(e.target.files?.[0])}
                    />
                    <Button
                      variant="secondary"
                      size="sm"
                      icon={importing ? 'hourglass_top' : 'upload_file'}
                      disabled={importing}
                      onClick={() => importFileRef.current?.click()}
                    >
                      {importing ? 'Reading sheet…' : importData ? 'Upload a different sheet' : 'Upload .xlsx / .csv'}
                    </Button>
                    {importData ? (
                      <div className="mt-3 flex flex-col gap-1.5 text-[12px] leading-[18px] text-cos-ink-secondary">
                        <p className="mono truncate text-cos-ink" title={importFileName}>{importFileName}</p>
                        <p>
                          Codes read from column {importData.column}
                          {importData.header_matched ? ' (matched the “Vendor Code” header)' : ' (no header found — first column used)'}
                          {importData.duplicates ? ` · ${num(importData.duplicates)} duplicate codes skipped` : ''}
                        </p>
                        {importData.missing.length > 0 && (
                          <details className="rounded-lg bg-[rgba(70,80,72,0.05)] p-2">
                            <summary className="cursor-pointer font-semibold text-cos-ink">
                              {num(importData.missing.length)} codes are not in the system
                            </summary>
                            <p className="mono mt-1.5 max-h-40 overflow-y-auto break-all text-[11px]">
                              {importData.missing.join(', ')}
                            </p>
                          </details>
                        )}
                      </div>
                    ) : (
                      <p className="mt-3 text-[12px] leading-[18px] text-cos-ink-secondary">
                        Any sheet with a vendor-code column works — the lot exports do as-is. Every code is looked
                        up in the vendor database; the sheet’s other columns are ignored.
                      </p>
                    )}
                  </div>
                ) : (
                <div className="rounded-xl border border-cos-line bg-white/50 p-4">
                  <div className="mb-3 flex items-center justify-between gap-2">
                    <span className="flex items-center gap-1.5 text-[13px] font-bold text-cos-ink">
                      <Icon name="filter_alt" className="text-[18px] text-cos-teal-deep" />
                      Filters
                      <span className="mono text-[11px] font-semibold text-cos-ink-secondary">{activeFilters} active</span>
                    </span>
                    <button type="button" onClick={resetFilters} className="text-[12px] font-semibold text-cos-ink-secondary hover:text-cos-ink">
                      Reset
                    </button>
                  </div>
                  <div className="flex flex-col gap-3">
                    <RailSelect label="Status" value={status} onChange={setStatus} options={opts.statuses ?? ['ACTIVE', 'PENDING', 'INACTIVE', 'SUSPENDED', 'REJECTED']} humanise={(v) => vendorPill(v).label} />
                    <RailSelect label="Category" value={category} onChange={setCategory} options={opts.categories ?? []} />
                    <RailSelect label="Type" value={type} onChange={setType} options={opts.types ?? []} />
                    <RailSelect label="Country" value={country} onChange={setCountry} options={opts.countries ?? []} />
                    <RailSelect label="MSME" value={msme} onChange={setMsme} options={opts.msme ?? ['MSME', 'NON_MSME', 'PENDING']} humanise={(v) => MSME_LABEL[v] || v} />
                    <RailSelect label="Region (India)" value={region} onChange={setRegion} options={opts.regions ?? []} />
                  </div>
                </div>
                )}
              </aside>

              {/* Main: every matching vendor, in full — the page scrolls, the list never does. */}
              <section className="flex min-w-0 flex-1 flex-col rounded-xl border border-cos-line bg-white/60">
                <div className="flex flex-wrap items-center gap-3 border-b border-cos-line-subtle px-4 py-3">
                  <div className="min-w-0">
                    <p className="text-[13px] font-bold text-cos-ink">
                      {audienceMode === 'import' ? 'Imported vendors' : 'Matching vendors'}
                    </p>
                    <p className="text-[12px] text-cos-ink-secondary">
                      {audienceMode === 'filters' && list.loading && list.data
                        ? 'Refreshing…'
                        : 'Untick anyone who should not receive this campaign.'}
                    </p>
                  </div>
                  {audienceMode === 'filters' && (
                    <SearchInput value={search} onChange={setSearch} placeholder="Search by company, vendor code or email…" className="ml-auto w-full max-w-md" />
                  )}
                  <span className="mono ml-auto shrink-0 text-[12px] text-cos-ink-secondary">
                    {audienceMode === 'import'
                      ? importData
                        ? `${num(importRows.length)} matched`
                        : ''
                      : list.data
                        ? `${num(listMeta.total)} match`
                        : ''}
                  </span>
                </div>
                <div>
                  <table className="w-full text-sm">
                    {/* Sticks to the top of the page while the rows scroll past. */}
                    <thead className="sticky top-0 z-10 bg-[rgba(250,249,244,0.96)] backdrop-blur">
                      <tr className="border-b border-cos-line">
                        <th className="w-10 px-3 py-2.5 text-left">
                          <PageCheckbox all={tablePageAll} some={tablePageSome} onChange={toggleTablePage} disabled={tableRows.length === 0} />
                        </th>
                        {['Vendor', 'Email', 'Category', 'Country', 'Status'].map((h) => (
                          <th key={h} className="whitespace-nowrap px-3 py-2.5 text-left text-2xs font-bold uppercase tracking-[0.08em] text-cos-ink-muted">
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {tableLoading ? (
                        Array.from({ length: 8 }, (_, i) => (
                          <tr key={i} className="border-b border-cos-line-subtle">
                            <td colSpan={6} className="px-3 py-2.5">
                              <div className="h-7 animate-pulse rounded-lg bg-[rgba(70,80,72,0.08)]" />
                            </td>
                          </tr>
                        ))
                      ) : audienceMode === 'filters' && list.error && !list.data ? (
                        <tr>
                          <td colSpan={6} className="px-3 py-10 text-center text-[13px] text-cos-status-coral">{list.error}</td>
                        </tr>
                      ) : tableRows.length === 0 ? (
                        <tr>
                          <td colSpan={6} className="px-3 py-14 text-center">
                            <div className="flex flex-col items-center gap-2 text-cos-ink-muted">
                              <Icon name={audienceMode === 'import' ? 'upload_file' : 'search_off'} className="text-[28px]" />
                              <span className="text-sm">
                                {audienceMode === 'import'
                                  ? 'Upload a spreadsheet to see the vendors it names.'
                                  : 'No vendors match these filters.'}
                              </span>
                            </div>
                          </td>
                        </tr>
                      ) : (
                        tableRows.map((v) => {
                          const ticked = isTicked(v.id);
                          const p = vendorPill(v.status);
                          return (
                            <tr
                              key={v.id}
                              onClick={() => toggleAny(v.id)}
                              className={[
                                'cursor-pointer border-b border-cos-line-subtle transition-colors last:border-0',
                                ticked ? 'hover:bg-[rgba(13,168,155,0.05)]' : 'bg-[rgba(70,80,72,0.04)] text-cos-ink-muted',
                              ].join(' ')}
                            >
                              <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                                <input
                                  type="checkbox"
                                  className={CHECKBOX}
                                  checked={ticked}
                                  onChange={() => toggleAny(v.id)}
                                  aria-label={`${ticked ? 'Untick' : 'Tick'} ${v.company_name}`}
                                />
                              </td>
                              <td className="px-3 py-2">
                                <div className={`font-semibold ${ticked ? 'text-cos-ink' : 'line-through decoration-cos-ink-muted/60'}`}>{v.company_name}</div>
                                <div className="mono text-2xs text-cos-ink-muted">{v.vendor_code || 'No vendor code'}</div>
                              </td>
                              <td className="mono px-3 py-2 text-2xs text-cos-ink-secondary">{v.email}</td>
                              <td className="px-3 py-2 text-cos-ink-secondary">{v.supplier_category || '—'}</td>
                              <td className="px-3 py-2 text-cos-ink-secondary">{v.country_origin || '—'}</td>
                              <td className="px-3 py-2">
                                <StatusPill status={p.pill} label={p.label} />
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
                {tableMeta.total > LIST_PAGE ? (
                  <div className="border-t border-cos-line-subtle px-4 py-2.5">
                    <Pagination page={tableMeta.page} pageSize={tableMeta.limit} total={tableMeta.total} onPageChange={setTablePage} noun="vendors" />
                  </div>
                ) : tableRows.length > 0 ? (
                  <div className="border-t border-cos-line-subtle px-4 py-2 text-[12px] text-cos-ink-secondary">
                    Showing all {num(tableMeta.total)} {audienceMode === 'import' ? 'imported' : 'matching'} vendors
                  </div>
                ) : null}
              </section>
            </div>
            </div>
          ) : null}

          {/* ---------------------------------------------------------- 4 review */}
          {step === 3 ? (
            <div className="grid grid-cols-1 items-start gap-4 py-1 lg:grid-cols-[340px_minmax(0,1fr)]">
              {/* Details stay in view while the mail scrolls past. */}
              <div className="sticky top-4 flex flex-col gap-3">
                {[
                  ['Campaign name', name.trim(), 'badge'],
                  ['Template', chosen?.name ?? '—', 'description'],
                  ['Subject', preview?.subject ?? '—', 'subject'],
                  ['Audience', audienceSummary, 'groups'],
                ].map(([k, v, icon]) => (
                  <div key={k} className="flex items-start gap-3 rounded-xl border border-cos-line bg-white/60 px-4 py-3">
                    <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-cos-status-teal-bg">
                      <Icon name={icon} className="text-[18px] text-cos-teal-deep" />
                    </span>
                    <div className="min-w-0">
                      <p className="text-[11px] font-semibold uppercase tracking-[1.2px] text-cos-ink-secondary">{k}</p>
                      <p className="text-sm font-semibold text-cos-ink">{v}</p>
                    </div>
                  </div>
                ))}
                {/* Addressing. In single_cc mode the TO is a literal address and the CC
                    list is the audience; in per_vendor mode CC stays the opt-in copy list. */}
                <div className="mb-3">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-cos-ink-muted">Greeting</p>
                  {chosen?.mode === 'scratch' ? (
                    <p className="mt-1 text-[11px] leading-[16px] text-cos-ink-secondary">
                      This template was written in the scratch pad, so its greeting is part of the mail itself.
                    </p>
                  ) : sendMode === 'single_cc' ? (
                    <p className="mt-1 text-[11px] leading-[16px] text-cos-ink-secondary">
                      One copy reaches every company at once, so it opens the same way for all of them.
                    </p>
                  ) : (
                    <select
                      value={greetingMode}
                      onChange={(e) => setGreetingMode(e.target.value)}
                      className={`${RAIL_FIELD} mt-1.5`}
                    >
                      <option value="vendor">Each vendor’s name — “Dear Acme Ltd,”</option>
                      <option value="vendor_code">Name and vendor code — “Dear Acme Ltd (VA00123),”</option>
                      <option value="fixed">The same words for everyone</option>
                    </select>
                  )}
                  {chosen?.mode !== 'scratch' && effectiveGreetingMode === 'fixed' ? (
                    <input
                      value={greetingText}
                      onChange={(e) => setGreetingText(e.target.value)}
                      placeholder="Dear Partners,"
                      className={`${RAIL_FIELD} mt-1.5`}
                    />
                  ) : null}
                </div>
                {sendMode === 'single_cc' ? (
                  <div className="rounded-xl border border-cos-teal/25 bg-cos-teal-faint/30 px-4 py-3">
                    <p className={RAIL_LABEL}>To — one email, one recipient</p>
                    <input
                      value={toEmail}
                      onChange={(e) => setToEmail(e.target.value)}
                      placeholder="you@genessence.ai"
                      className={RAIL_FIELD}
                    />
                    <input
                      value={toName}
                      onChange={(e) => setToName(e.target.value)}
                      placeholder="Display name for the TO (optional)"
                      className={`${RAIL_FIELD} mt-1.5`}
                    />
                    <p className="mt-1 text-[11px] leading-[16px] text-cos-ink-secondary">
                      One mail reaches every company at once, so the greeting above is the same for
                      everyone copied, and there are no per-vendor details anywhere in it.
                    </p>
                    {copyList.length > 0 ? (
                      <select
                        value=""
                        onChange={(e) => {
                          const picked = copyList.find((c) => c.email === e.target.value);
                          if (!picked) return;
                          // Promoting someone to TO takes them out of BOTH copy lists: one
                          // address appearing twice across to/cc/bcc is a duplicate the
                          // provider refuses outright, failing the entire send.
                          const drop = (list) =>
                            list.filter((c) => c.email.toLowerCase() !== picked.email.toLowerCase());
                          setToEmail(picked.email);
                          setToName(picked.label || '');
                          setCcEntries(drop);
                          setBccEntries(drop);
                        }}
                        className={`${RAIL_FIELD} mt-1.5`}
                      >
                        <option value="">Or pick someone already on a copy list…</option>
                        {copyList.map((c) => (
                          <option key={c.email} value={c.email}>
                            {c.label ? `${c.label} — ${c.email}` : c.email}
                          </option>
                        ))}
                      </select>
                    ) : null}
                  </div>
                ) : (
                  <div className="rounded-xl border border-cos-line bg-white/60 px-4 py-3">
                    <label className="flex cursor-pointer items-center gap-2 text-[13px] font-bold text-cos-ink">
                      <input
                        type="checkbox"
                        className={CHECKBOX}
                        checked={ccEnabled}
                        onChange={(e) => setCcEnabled(e.target.checked)}
                      />
                      CC copies of every email
                    </label>
                    {ccEnabled ? (
                      <p className="mt-1.5 text-[11px] leading-[16px] text-cos-ink-secondary">
                        Every address below receives a copy of each of the {num(recipientCount)} emails.
                      </p>
                    ) : null}
                  </div>
                )}

                {ccActive ? (
                  <>
                    <RecipientListEditor
                      label="CC"
                      entries={ccEntries}
                      onChange={setCcEntries}
                      onFillFromAudience={fillFromAudience(setCcEntries, setCcLoading)}
                      onUploadFile={uploadSheet(setCcEntries, setCcLoading)}
                      max={MAX_CC}
                      loading={ccLoading}
                      capped={ccCapped}
                      onError={toast.warning}
                      emptyHint={
                        'Nobody is copied yet. “Fill from audience” brings in the vendors you selected, ' +
                        '“Upload” reads a sheet, and the box below takes addresses one at a time.'
                      }
                      footnote={
                        ccEntries.length > 0
                          ? 'Everyone in CC can see every other address on this list. Use BCC to keep them hidden.'
                          : null
                      }
                    />
                    <RecipientListEditor
                      label="BCC"
                      entries={bccEntries}
                      onChange={setBccEntries}
                      onFillFromAudience={fillFromAudience(setBccEntries, setBccLoading)}
                      onUploadFile={uploadSheet(setBccEntries, setBccLoading)}
                      max={MAX_CC}
                      loading={bccLoading}
                      capped={bccCapped}
                      takenElsewhere={[toEmail, ...ccList]}
                      onError={toast.warning}
                      emptyHint={
                        'Blind copies. Same spreadsheet and the same audience fill as CC, except nobody on this ' +
                        'list is visible to the recipients or to each other — which is usually what you want ' +
                        'when the copy list is the vendor base itself.'
                      }
                      footnote={
                        bccEntries.length > 0
                          ? 'Hidden from everyone, including each other. Its own limit, so CC and BCC together reach ' +
                            `${num(MAX_CC * 2)} addresses.`
                          : null
                      }
                    />
                  </>
                ) : null}

                {ccBlocker ? (
                  <p className="rounded-xl border border-cos-status-amber/30 bg-cos-status-amber-bg px-4 py-2.5 text-[12px] leading-[17px] text-cos-status-amber">
                    {ccBlocker}
                  </p>
                ) : null}
                {preview?.sample_recipients?.length ? (
                  <p className="text-[12px] leading-[18px] text-cos-ink-secondary">
                    Going to {preview.sample_recipients.join(', ')}
                    {preview.recipient_count > preview.sample_recipients.length
                      ? ` and ${num(preview.recipient_count - preview.sample_recipients.length)} more`
                      : ''}
                  </p>
                ) : null}
                <button
                  type="button"
                  onClick={() => setStep(2)}
                  className="self-start text-[13px] font-semibold text-cos-teal-deep hover:underline"
                >
                  <Icon name="edit" className="mr-1 align-middle text-[15px]" />
                  Change audience
                </button>
              </div>

              {/* The mail, whole and at true size. It is as tall as it is; the page scrolls. */}
              <div className="overflow-hidden rounded-xl border border-cos-line">
                <div className="flex items-center gap-2 border-b border-cos-line-subtle bg-white/70 px-4 py-2.5">
                  <Icon name="mark_email_read" className="text-[18px] text-cos-teal-deep" />
                  <span className="truncate text-[13px] font-semibold text-cos-ink">
                    {sendMode === 'single_cc'
                      ? 'Exactly what everyone copied receives'
                      : preview?.sample?.label
                        ? `Exactly what ${preview.sample.label} receives`
                        : 'Exactly what each vendor receives'}
                  </span>
                  <span className="mono ml-auto truncate text-[12px] text-cos-ink-secondary">{preview?.subject}</span>
                </div>
                <div className="py-4" style={{ background: EMAIL_CANVAS }}>
                  <EmailPreview html={preview?.html} title="Campaign preview" />
                </div>
              </div>
            </div>
          ) : null}

          {/* ---------------------------------------------------------- 5 launch */}
          {step === 4 ? (
            <div className="flex flex-col items-center gap-3 py-10 text-center">
              <span className="grid h-16 w-16 place-items-center rounded-2xl bg-gradient-to-b from-cos-teal-mid to-cos-teal-deep text-white">
                <Icon name="rocket_launch" filled className="text-[30px]" />
              </span>
              <h3 className="mt-2 text-xl font-bold text-cos-ink">Ready to launch</h3>
              <p className="max-w-[460px] text-sm leading-[21px] text-cos-ink-secondary">
                <strong className="text-cos-ink">{name.trim()}</strong> will be emailed to{' '}
                <strong className="text-cos-teal-deep">{num(recipientCount)}</strong> vendors as “{preview?.subject}”.
                {excludedMatching.length ? ` ${num(excludedMatching.length)} unticked vendor(s) will not receive it.` : ''} Emails cannot be
                recalled once sent.
              </p>
              <div className="mt-2 grid grid-cols-3 gap-3">
                {[
                  [num(recipientCount), 'Vendors'],
                  [chosen?.name ?? '—', 'Template'],
                  ['Email', 'Channel'],
                ].map(([v, k]) => (
                  <div key={k} className="rounded-xl border border-cos-line bg-white/60 px-5 py-3">
                    <p className="mono text-[15px] font-bold text-cos-ink">{v}</p>
                    <p className="text-[11px] uppercase tracking-wide text-cos-ink-secondary">{k}</p>
                  </div>
                ))}
              </div>

              <PreSendCheck
                result={precheck}
                loading={prechecking}
                onDropFromCopyLists={dropAddressesFromCopyLists}
              />
            </div>
          ) : null}
        </div>

        {/* Pinned to the bottom of the viewport while the page scrolls, so Back / Continue never leave reach. */}
        <div className="sticky bottom-0 z-20 flex items-center justify-between gap-3 rounded-b-2xl border-t border-cos-line bg-[rgba(250,249,244,0.94)] px-6 py-4 backdrop-blur">
          <Button variant="ghost" icon="chevron_left" onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0 || sending}>
            Back
          </Button>
          <div className="flex items-center gap-3">
            <span className="mono text-[12px] text-cos-ink-secondary">Step {step + 1} of {STEPS.length}</span>
            {step < STEPS.length - 1 ? (
              <Button variant="primary" iconRight="chevron_right" onClick={next} disabled={!canContinue || counting || sending}>
                {counting && step === 2 ? 'Counting…' : 'Continue'}
              </Button>
            ) : (
              <Button
                variant="primary"
                icon="rocket_launch"
                onClick={launch}
                disabled={sending || !recipientCount || hourlyBlocked}
                title={hourlyBlocked ? 'This mail carries more addresses than the account can send in an hour' : undefined}
              >
                {sending ? 'Launching…' : 'Launch Campaign'}
              </Button>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
}

export default NewCampaignWizard;
